import React, { useEffect, useState } from 'react';

// All spinner images that need to be preloaded
const spinnerImages = [
  '/images/colabottle.png',
  '/images/orangesodabottle.png',
  '/images/waterbottle.png',
  '/images/beerbottle.png',
  '/images/whiskybottle.png',
  '/images/champagne.png',
  '/images/pencil.png',
  '/images/pen.png',
  '/images/compass.png',
  '/images/knife.png',
  '/images/arrow.png',
  '/images/magic-wand.png',
];

// Game card images that need to be preloaded
const gameCardImages = [
  '/images/TruthEmoji.png',
  '/images/Trivia.png',
  '/images/WhichOneYouAre.png',
  '/images/WouldYouRather.png',
  '/images/DareEmoji.png',
];

// All images to preload
const allImages = [...spinnerImages, ...gameCardImages];

interface ImagePreloaderProps {
  onComplete?: () => void;
  showProgress?: boolean;
}

const ImagePreloader: React.FC<ImagePreloaderProps> = ({ 
  onComplete, 
  showProgress = false 
}) => {
  const [loadedCount, setLoadedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let mounted = true;
    let completedCount = 0;
    let failedCount = 0;

    const preloadImage = (src: string): Promise<void> => {
      return new Promise((resolve) => {
        const img = new Image();
        
        img.onload = () => {
          if (mounted) {
            completedCount++;
            setLoadedCount(completedCount);
            console.log(`✅ Preloaded image: ${src}`);
          }
          resolve();
        };
        
        img.onerror = () => {
          if (mounted) {
            failedCount++;
            setFailedCount(failedCount);
            console.warn(`❌ Failed to preload image: ${src}`);
          }
          resolve(); // Resolve even on error to continue with other images
        };
        
        // Start loading the image
        img.src = src;
      });
    };

    const preloadAllImages = async () => {
      try {
        // Preload all images concurrently
        await Promise.all(allImages.map(preloadImage));
        
        if (mounted) {
          setIsComplete(true);
          console.log(`🎉 Image preloading complete! Loaded: ${completedCount}, Failed: ${failedCount}`);
          onComplete?.();
        }
      } catch (error) {
        console.error('Error during image preloading:', error);
        if (mounted) {
          setIsComplete(true);
          onComplete?.();
        }
      }
    };

    // Start preloading
    preloadAllImages();

    return () => {
      mounted = false;
    };
  }, [onComplete]);

  // Don't render anything if not showing progress
  if (!showProgress) {
    return null;
  }

  const totalImages = allImages.length;
  const progress = totalImages > 0 ? ((loadedCount + failedCount) / totalImages) * 100 : 0;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      color: 'white',
      fontFamily: 'Arial, sans-serif',
    }}>
      <div style={{
        fontSize: '24px',
        marginBottom: '20px',
        textAlign: 'center',
      }}>
        Loading Images...
      </div>
      
      <div style={{
        width: '300px',
        height: '20px',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '10px',
        overflow: 'hidden',
        marginBottom: '10px',
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          backgroundColor: '#4CAF50',
          transition: 'width 0.3s ease',
        }} />
      </div>
      
      <div style={{
        fontSize: '14px',
        textAlign: 'center',
      }}>
        {loadedCount + failedCount} / {totalImages} images loaded
        {failedCount > 0 && (
          <div style={{ color: '#ff6b6b', marginTop: '5px' }}>
            {failedCount} failed to load
          </div>
        )}
      </div>
      
      {isComplete && (
        <div style={{
          fontSize: '16px',
          color: '#4CAF50',
          marginTop: '10px',
          animation: 'fadeIn 0.5s ease-in',
        }}>
          Ready to spin! 🎉
        </div>
      )}
      
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default ImagePreloader;
